import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'

import logger from '@/utils/logger'

import RPC from '@/client/rpc'
import getLocalStorage from '@/client/services/browserStorage/getLocalStorage'

const KEY_FCM_TOKEN_INFO = 'shw-fcm-token'

export default function useFcmToken(): {
  register: (token: string) => void
  clear: () => void
} {
  const storage = useMemo(() => getLocalStorage(KEY_FCM_TOKEN_INFO), [])

  const register = useCallback(
    (token: string) => {
      try {
        let lastInfo: { token: string; ts: string } | null = null
        try {
          lastInfo = JSON.parse(storage.get() ?? '')
        } catch (_) {
          // No-op
        }

        let shouldUpdate = false

        if (lastInfo == null) {
          shouldUpdate = true
        } else {
          const diffSince = DateTime.fromISO(lastInfo.ts).diffNow('hours')
          const hoursSince = diffSince.hours * -1

          shouldUpdate = !diffSince.isValid || hoursSince >= 1 || token !== lastInfo.token
        }

        if (shouldUpdate) {
          storage.set(JSON.stringify({ token, ts: new Date().toISOString() }))
          RPC.registerFcmToken({ token }).catch(err => {
            logger.error('Error registering FCM token via API', { err })
          })
        }
      } catch (err) {
        logger.error('Error registering FCM token', { err })
        // Empty state in case it is malformed
        storage.del()
      }
    },
    [storage]
  )

  const clear = useCallback(() => {
    storage.del()
  }, [storage])

  return useMemo(() => ({ register, clear }), [register, clear])
}
